import React, { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Phone, Clock, User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function ActiveCallsWidget() {
  const [activeCalls, setActiveCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  
  // Ref para atualizar duração visual sem fazer fetch
  const durationIntervalRef = useRef(null);

  useEffect(() => {
    loadActiveCalls();

    // Set up real-time subscription
    const channel = supabase
      .channel('active-calls-widget')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'calls',
          filter: 'status=in.(ringing,in-progress)',
        },
        (payload) => {
          console.log('Active calls changed via Realtime:', payload.eventType);
          
          if (payload.eventType === 'INSERT') {
            // Nova chamada ativa - adicionar ao estado
            loadCallWithDetails(payload.new.id);
          } else if (payload.eventType === 'UPDATE') {
            // Atualização de chamada
            if (['ringing', 'in-progress'].includes(payload.new.status)) {
              // Ainda ativa - atualizar
              setActiveCalls(prev => prev.map(call => 
                call.id === payload.new.id ? { ...call, ...payload.new } : call
              ));
            } else {
              // Não mais ativa - remover
              setActiveCalls(prev => prev.filter(call => call.id !== payload.new.id));
            }
          } else if (payload.eventType === 'DELETE') {
            // Chamada removida
            setActiveCalls(prev => prev.filter(call => call.id !== payload.old.id));
          }
          
          setLastUpdate(new Date());
        }
      )
      .subscribe();

    // ============================================================
    // REMOVIDO: Polling de 10 segundos - o Realtime já cuida disso
    // Mantido apenas intervalo para atualizar VISUALMENTE as durações
    // (isso não faz fetch, apenas re-renderiza o componente)
    // ============================================================
    durationIntervalRef.current = setInterval(() => {
      // Força re-render para atualizar durações exibidas
      setLastUpdate(new Date());
    }, 10000);

    return () => {
      supabase.removeChannel(channel);
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, []);

  // Carregar uma chamada específica com detalhes (para INSERTs do Realtime)
  const loadCallWithDetails = async (callId) => {
    try {
      const { data: call, error } = await supabase
        .from('calls')
        .select(`
          id,
          direction,
          from_e164,
          to_e164,
          status,
          started_at,
          created_at,
          title,
          contact_id,
          contacts:contact_id (
            first_name,
            last_name,
            primary_phone
          ),
          lead_id,
          leads:lead_id (
            first_name,
            last_name
          )
        `)
        .eq('id', callId)
        .single();

      if (error) throw error;

      if (call && ['ringing', 'in-progress'].includes(call.status)) {
        setActiveCalls(prev => {
          // Evitar duplicatas
          if (prev.some(c => c.id === call.id)) {
            return prev.map(c => c.id === call.id ? call : c);
          }
          return [call, ...prev];
        });
      }
    } catch (err) {
      console.error('Error loading call details:', err);
    }
  };

  const loadActiveCalls = async () => {
    try {
      setLoading(true);

      // Calculate cutoff time - only show calls from last 4 hours
      const fourHoursAgo = new Date();
      fourHoursAgo.setHours(fourHoursAgo.getHours() - 4);

      const { data: calls, error } = await supabase
        .from('calls')
        .select(`
          id,
          direction,
          from_e164,
          to_e164,
          status,
          started_at,
          created_at,
          title,
          contact_id,
          contacts:contact_id (
            first_name,
            last_name,
            primary_phone
          ),
          lead_id,
          leads:lead_id (
            first_name,
            last_name
          )
        `)
        .in('status', ['ringing', 'in-progress'])
        .or(`started_at.gte.${fourHoursAgo.toISOString()},and(started_at.is.null,created_at.gte.${fourHoursAgo.toISOString()})`)
        .order('started_at', { ascending: false, nullsFirst: true });

      if (error) {
        console.error('Error loading active calls:', error);
        throw error;
      }

      console.log('Active calls loaded (last 4 hours):', calls?.length || 0);

      setActiveCalls(calls || []);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Error loading active calls:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCallDuration = (startedAt) => {
    if (!startedAt) return 'Just now';
    return formatDistanceToNow(new Date(startedAt), { 
      addSuffix: false,
      locale: ptBR 
    });
  };

  const getContactName = (call) => {
    if (call.title) return call.title;
    
    if (call.contacts) {
      return `${call.contacts.first_name || ''} ${call.contacts.last_name || ''}`.trim() || 'Unknown';
    }
    
    if (call.leads) {
      return `${call.leads.first_name || ''} ${call.leads.last_name || ''}`.trim() || 'Unknown';
    }
    
    return call.direction === 'inbound' ? call.from_e164 : call.to_e164;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ringing':
        return 'bg-yellow-500';
      case 'in-progress':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'ringing':
        return 'Ringing';
      case 'in-progress':
        return 'In Progress';
      default:
        return status;
    }
  };

  if (loading && activeCalls.length === 0) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Phone className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Active Calls</h3>
            <p className="text-sm text-muted-foreground">Real-time call monitoring</p>
          </div>
        </div>
        <div className="text-center text-muted-foreground py-8">
          Loading...
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center relative">
            <Phone className="h-5 w-5 text-primary" />
            {activeCalls.length > 0 && (
              <span className="absolute -top-1 -right-1 h-4 w-4 bg-green-500 rounded-full border-2 border-background animate-pulse" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold">Active Calls</h3>
            <p className="text-sm text-muted-foreground">Real-time monitoring</p>
          </div>
        </div>
        <Badge variant="secondary" className="text-lg font-semibold">
          {activeCalls.length}
        </Badge>
      </div>

      <div className="space-y-3">
        {activeCalls.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Phone className="h-12 w-12 mx-auto mb-2 opacity-20" />
            <p>No active calls at the moment</p>
          </div>
        ) : (
          activeCalls.map((call) => (
            <div
              key={call.id}
              className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
            >
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-primary/10 text-primary">
                  <User className="h-5 w-5" />
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-medium truncate">
                    {getContactName(call)}
                  </p>
                  <Badge 
                    variant="secondary" 
                    className="text-xs"
                  >
                    {call.direction === 'inbound' ? '↓ Inbound' : '↑ Outbound'}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  <span>{getCallDuration(call.started_at)}</span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className={`h-2 w-2 rounded-full ${getStatusColor(call.status)} animate-pulse`} />
                <span className="text-xs font-medium text-muted-foreground">
                  {getStatusLabel(call.status)}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {activeCalls.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <p className="text-xs text-muted-foreground text-center">
            Updates automatically via Realtime • Last update: {lastUpdate.toLocaleTimeString()}
          </p>
        </div>
      )}
    </Card>
  );
}
